import { Section } from '@/components/ui/Section';
import {
  buildLinearGradient,
  getGradientBackground,
  getSlideBackgroundKind,
  getSolidBackground,
  SLIDE_GRADIENT_PRESETS,
  type SlideLinearGradient,
} from '@/lib/slide-background';
import { cn } from '@/lib/utils';

interface Props {
  value?: string;
  onChange: (background: string) => void;
}

/** Slide background editor: solid colour or two-stop linear gradient. */
export function SlideBackgroundEditor({ value, onChange }: Props) {
  const kind = getSlideBackgroundKind(value);
  const solid = getSolidBackground(value);
  const gradient = getGradientBackground(value);

  const setGradient = (g: SlideLinearGradient) => onChange(buildLinearGradient(g));

  return (
    <Section title="Background">
      <div className="mb-2 inline-flex rounded-md border border-border p-0.5 text-xs">
        <Tab active={kind === 'solid'} onClick={() => onChange(solid)}>
          Solid
        </Tab>
        <Tab active={kind === 'gradient'} onClick={() => setGradient(gradient)}>
          Gradient
        </Tab>
      </div>

      {kind === 'solid' ? (
        <div className="flex h-9 items-center gap-2 rounded-md border border-border bg-white pl-2 pr-3">
          <input
            type="color"
            value={solid}
            onChange={(e) => onChange(e.target.value)}
            className="h-6 w-7 cursor-pointer rounded-sm border border-border bg-transparent"
            aria-label="Background color"
          />
          <input
            value={solid}
            onChange={(e) => onChange(e.target.value)}
            className="flex-1 bg-transparent text-sm text-fg outline-none"
          />
        </div>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <GradientColor
              label="From"
              value={gradient.from}
              onChange={(from) => setGradient({ ...gradient, from })}
            />
            <GradientColor
              label="To"
              value={gradient.to}
              onChange={(to) => setGradient({ ...gradient, to })}
            />
          </div>
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium text-fg-muted">
              Angle — {gradient.angle}°
            </span>
            <input
              type="range"
              min={0}
              max={360}
              value={gradient.angle}
              onChange={(e) => setGradient({ ...gradient, angle: Number(e.target.value) })}
              className="w-full"
            />
          </label>
          <div className="flex flex-wrap gap-1.5">
            {SLIDE_GRADIENT_PRESETS.map((p) => (
              <button
                key={p.label}
                title={p.label}
                onClick={() => setGradient(p.gradient)}
                className="h-7 w-10 rounded-md border border-border"
                style={{ background: buildLinearGradient(p.gradient) }}
              />
            ))}
          </div>
        </div>
      )}
    </Section>
  );
}

function Tab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'rounded px-2.5 py-1 font-medium transition',
        active ? 'bg-accent text-white' : 'text-fg-muted hover:text-fg',
      )}
    >
      {children}
    </button>
  );
}

function GradientColor({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium text-fg-muted">{label}</span>
      <div className="flex h-9 items-center gap-2 rounded-md border border-border bg-white pl-2 pr-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-6 w-7 cursor-pointer rounded-sm border border-border bg-transparent"
          aria-label={`${label} color`}
        />
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent text-xs text-fg outline-none"
        />
      </div>
    </label>
  );
}
